/**
 * Instrumentation « En vedette » — Love'nSpa
 * Fichier à déposer dans le thème : assets/loven-vedette-tracking.js
 *
 * Mesure trois choses, et rien d'autre :
 *   card_impression    : une carte hébergement a été réellement vue, boostée ou non
 *   vedette_impression — une carte en vedette a été réellement vue
 *   vedette_clic       — un voyageur a cliqué sur une carte en vedette
 *
 * POURQUOI card_impression (18/09/2026)
 * Le thème savait mesurer le clic sur une carte (select_item, lns-engage.js)
 * mais jamais son affichage, sauf pour les places payées. Sans dénominateur,
 * impossible de dire à un hébergeur que sa photo principale attire moins de
 * clics que celles de ses voisins : c'est l'indicateur central du levier
 * « visibilité interne » du dashboard hébergeur.
 *
 * Il est émis ICI et pas dans un fichier neuf, parce que tout ce qui rend
 * cette mesure difficile est déjà traité dans ce fichier : les deux grilles
 * qui coexistent, les cartes masquées par le filtre géo ou la carte Leaflet,
 * les six repeints qui recréent le DOM, la déduplication par visite. Un second
 * observateur écrit à côté referait ces erreurs une par une.
 *
 * vedette_impression n'est PAS remplacé. Il alimente l'écran d'administration
 * des boosts et v_media_occupation, avec sa position DANS LE BLOC payé, qui
 * n'est pas la position dans la page. Les deux séries coexistent et ne
 * s'additionnent jamais : le portail hébergeur ne lit que card_impression,
 * filtré sur position_type quand il veut les boosts.
 *
 * DEUX SURFACES, DEUX FAÇONS DE RECONNAÎTRE UNE PLACE PAYÉE :
 *
 *   a) Pages de collection et de recherche — « surface: collection / recherche »
 *      Les logements en vedette sont choisis côté JS par collection-top-ads.js
 *      et signalés par la pill [data-loven-spons] posée par
 *      loven-sponsored-badges.js. Une carte est en vedette si elle porte la pill.
 *
 *   b) Page d'accueil — « surface: accueil »
 *      Le bloc « Love rooms à la Une » est une collection manuelle
 *      (mise-en-avant-page-daccueil) dont TOUTES les cartes sont des places
 *      vendues. Il n'y a donc pas de pill : le bloc entier est marqué en Liquid
 *      par data-loven-vedette-bloc="<handle de la collection>", et chaque carte
 *      qu'il contient est comptée.
 *
 * Ce que ce fichier NE fait PAS, volontairement :
 *   - il ne choisit pas les logements mis en avant (collection-top-ads.js),
 *   - il ne pose aucun badge (loven-sponsored-badges.js),
 *   - il ne stocke rien de persistant : il réutilise le session_id de
 *     lovenspa-funnel.js, en sessionStorage, non persistant entre visites.
 *
 * Contraintes du thème dont dépend ce code, vérifiées à la lecture :
 *   1. Le registre Liquid #loven-partner-ads-registry-json a toujours
 *      "campaigns": [] — les campagnes arrivent par Supabase, côté JS.
 *      La seule source fiable est l'événement `loven:sponsored-ready`.
 *   2. Une carte en vedette porte la pill [data-loven-spons], posée dans
 *      .grid-product__image-wrap par loven-sponsored-badges.js.
 *   3. paint() est rejoué sur au moins six événements et RETIRE les pills
 *      devenues inéligibles. Compter une impression à la pose du DOM
 *      produirait des doublons massifs : on ne compte que la visibilité réelle,
 *      et on déduplique par (produit, surface) pour la durée de la visite.
 *   4. Deux grilles coexistent — la statique reste dans le DOM, masquée en CSS,
 *      quand la grille de disponibilité prend le relais. On s'appuie sur
 *      window.LovenGetProductGridCards() quand elle existe, et on vérifie la
 *      visibilité effective de chaque carte.
 *   5. Le tirage des logements en vedette est aléatoire quand il y a plus
 *      d'éligibles que de places. L'impression ne peut donc pas se déduire
 *      côté serveur : elle doit être mesurée ici.
 *   6. La page d'accueil porte d'autres grilles (carrousels régionaux de la
 *      section featured-collections). Elles ne sont PAS payées : seules les
 *      cartes situées dans un conteneur marqué data-loven-vedette-bloc sont
 *      comptées, et la position est calculée à l'intérieur de ce bloc.
 */
(function () {
  'use strict';

  var VEDETTE = window.LNS_VEDETTE;
  if (!VEDETTE) return;

  var BLOC = '[data-loven-vedette-bloc]';
  var SEUIL_VISIBLE = 0.5; // moitié de la carte dans le viewport
  var DUREE_MS = 1000; // et pendant une seconde pleine

  // Déduplication pour la durée de la visite : une impression par couple
  // (produit, surface). Le rechargement de la grille ne recompte pas.
  var impressionsEmises = Object.create(null);
  // Registre distinct pour card_impression : sa clé est le produit seul, sans
  // la surface, parce que la grille d'une collection est une seule liste même
  // quand la carte y entre et en sort au gré des filtres.
  var cartesEmises = Object.create(null);
  var nbCartesEmises = 0;
  // Plafond de sécurité. Un visiteur qui fait défiler longuement une
  // collection de 250 logements produirait autant de lignes en base pour une
  // information que les 60 premières décrivent déjà. Le plafond est un
  // garde-fou de volume, pas un seuil analytique.
  var PLAFOND_CARTES = 60;
  var observees = new WeakSet();
  var minuteurs = new WeakMap();
  var observateur = null;

  function track(type, payload, options) {
    var f = window.LNS_FUNNEL;
    if (f && typeof f.track === 'function') {
      f.track(type, payload, options);
      return;
    }
    // lovenspa-funnel.js pas encore chargé : sa file d'attente prend le relais.
    window.LNS_FUNNEL_QUEUE = window.LNS_FUNNEL_QUEUE || [];
    window.LNS_FUNNEL_QUEUE.push([type, payload, options]);
  }

  /** Le conteneur de bloc payé auquel appartient la carte, ou null. */
  function blocDe(carte) {
    if (!carte || !carte.closest) return null;
    return carte.closest(BLOC);
  }

  /** Une carte est-elle une place payée ? */
  function estVedette(carte) {
    if (blocDe(carte)) return true;
    return !!(carte.querySelector && carte.querySelector('[data-loven-spons]'));
  }

  function surfaceDe(bloc) {
    if (bloc) return VEDETTE.pageType === 'index' ? 'accueil' : 'bloc';
    return VEDETTE.pageType === 'search' ? 'recherche' : 'collection';
  }

  /** Le handle de collection qui identifie la surface facturable. */
  function handleDe(bloc) {
    if (bloc) return bloc.getAttribute('data-loven-vedette-bloc') || null;
    return VEDETTE.collectionHandle || null;
  }

  function contexteVedette(carte, bloc, position, total) {
    return {
      source: 'vedette',
      surface: surfaceDe(bloc),
      collection_handle: handleDe(bloc),
      collection_id: bloc ? null : VEDETTE.collectionId || null,
      product_id: carte.getAttribute('data-product-id') || null,
      product_handle: carte.getAttribute('data-product-handle') || null,
      position: position,
      nb_vedettes: total
    };
  }

  /** Une carte n'est comptée que si elle est réellement affichable. */
  function carteVisible(carte) {
    if (!carte || !carte.isConnected) return false;
    if (carte.classList.contains('loven-geo-hidden')) return false;
    if (carte.classList.contains('loven-map-hidden')) return false;
    // offsetParent est null dès qu'un ancêtre est en display:none — c'est le cas
    // de la grille statique quand la grille de disponibilité prend la main.
    return carte.offsetParent !== null;
  }

  /** Toutes les cartes produit du DOM, dans l'ordre d'affichage. */
  function toutesLesCartes() {
    var toutes = [];
    if (typeof window.LovenGetProductGridCards === 'function') {
      try {
        toutes = Array.prototype.slice.call(window.LovenGetProductGridCards() || []);
      } catch (e) {
        toutes = [];
      }
    }
    if (!toutes.length) {
      toutes = Array.prototype.slice.call(
        document.querySelectorAll('.grid-product[data-product-id]')
      );
    }
    return toutes;
  }

  /**
   * Les cartes en vedette d'un groupe donné, dans l'ordre d'affichage.
   * `bloc` = null pour la surface collection/recherche (détection par pill),
   * ou l'élément conteneur pour un bloc entièrement payé.
   * La position vendue est une position DANS LE BLOC, jamais dans la page.
   */
  function vedettesDuGroupe(bloc) {
    var toutes = toutesLesCartes();
    var res = [];
    for (var i = 0; i < toutes.length; i++) {
      var carte = toutes[i];
      if (!carteVisible(carte)) continue;
      if (blocDe(carte) !== bloc) continue;
      if (!bloc && !carte.querySelector('[data-loven-spons]')) continue;
      res.push(carte);
    }
    return res;
  }

  // toutesLesVedettes() a été retirée le 18/09/2026 : brancher() observe
  // désormais toutes les cartes visibles, et vedettesDuGroupe() suffit au
  // calcul de la position vendue au moment de l'émission.

  function cleImpression(carte, bloc) {
    return (
      (carte.getAttribute('data-product-id') || '?') +
      '@' +
      (handleDe(bloc) || surfaceDe(bloc))
    );
  }

  function creerObservateur() {
    if (observateur || typeof IntersectionObserver === 'undefined') return;
    observateur = new IntersectionObserver(
      function (entrees) {
        entrees.forEach(function (entree) {
          var carte = entree.target;
          if (entree.isIntersecting && entree.intersectionRatio >= SEUIL_VISIBLE) {
            if (minuteurs.has(carte)) return;
            minuteurs.set(
              carte,
              window.setTimeout(function () {
                minuteurs.delete(carte);
                emettreImpression(carte);
              }, DUREE_MS)
            );
          } else {
            var t = minuteurs.get(carte);
            if (t) {
              window.clearTimeout(t);
              minuteurs.delete(carte);
            }
          }
        });
      },
      { threshold: [SEUIL_VISIBLE] }
    );
  }

  /** Le handle de la liste courante, tel que le portera item_list_id. */
  function listeCourante() {
    if (VEDETTE.pageType === 'search') return 'search';
    return VEDETTE.collectionHandle || VEDETTE.pageType || null;
  }

  /**
   * card_impression : toutes les cartes, boostées ou non.
   * La position est celle DANS LA PAGE, 1-indexée, et non dans le bloc payé :
   * c'est le rang que le visiteur voit, et le seul comparable d'une carte à
   * l'autre. `position_type` dit si la place a été payée.
   */
  function emettreCarte(carte) {
    if (!carteVisible(carte)) return;
    var handle = carte.getAttribute('data-product-handle');
    if (!handle) return;
    if (cartesEmises[handle]) return;
    if (nbCartesEmises >= PLAFOND_CARTES) return;
    cartesEmises[handle] = true;
    nbCartesEmises++;

    var toutes = toutesLesCartes().filter(carteVisible);
    var rang = toutes.indexOf(carte);
    track('card_impression', {
      item_id: handle,
      item_list_id: listeCourante(),
      index: rang >= 0 ? rang + 1 : null,
      position_type: estVedette(carte) ? 'boost' : 'organic',
      product_id: carte.getAttribute('data-product-id') || null,
      nb_cartes: toutes.length
    });
  }

  function emettreImpression(carte) {
    if (!carteVisible(carte)) return;

    // Toute carte vue compte pour le dénominateur, payée ou non.
    emettreCarte(carte);

    // La pill a pu être retirée entre-temps par un repaint : on revérifie.
    if (!estVedette(carte)) return;

    var bloc = blocDe(carte);
    var cle = cleImpression(carte, bloc);
    if (impressionsEmises[cle]) return;
    impressionsEmises[cle] = true;

    var groupe = vedettesDuGroupe(bloc);
    var position = groupe.indexOf(carte);
    track(
      'vedette_impression',
      contexteVedette(carte, bloc, position >= 0 ? position + 1 : null, groupe.length)
    );
  }

  /**
   * view_item_list : le pendant GA4 de card_impression, émis UNE FOIS par page.
   *
   * card_impression ne part jamais dans dataLayer. La cardinalité du triplet
   * item_id x item_list_id x index se compte en millions de combinaisons, et
   * GA4 renverrait « (other) » exactement sur la dimension utile, pour deux à
   * trois millions d'événements par mois. Le dashboard hébergeur lit Supabase,
   * pas GA4 : rien n'y perd.
   *
   * GA4 reçoit à la place l'événement e-commerce standard, avec la grille
   * telle qu'elle est servie. Un événement au lieu de quarante-huit, et le
   * taux de clic liste devient lisible dans les rapports natifs sans aucune
   * configuration. C'est la liste qui est vue ici, pas les cartes une à une :
   * c'est exactement ce que view_item_list veut dire.
   */
  var listeEmise = false;

  function emettreListeGA4() {
    if (listeEmise) return;
    var cartes = toutesLesCartes().filter(carteVisible);
    if (!cartes.length) return;
    listeEmise = true;

    var items = [];
    for (var i = 0; i < cartes.length && i < PLAFOND_CARTES; i++) {
      var handle = cartes[i].getAttribute('data-product-handle');
      if (!handle) continue;
      items.push({
        item_id: handle,
        item_list_id: listeCourante(),
        index: i + 1
      });
    }
    if (!items.length) return;

    try {
      window.dataLayer = window.dataLayer || [];
      // Purge obligatoire : sans elle, GA4 relit l'objet ecommerce du message
      // précédent et attribue ses articles à cet événement.
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: { item_list_id: listeCourante(), items: items }
      });
    } catch (e) { /* jamais bloquant */ }
  }

  function brancher() {
    creerObservateur();
    emettreListeGA4();
    // On observe désormais TOUTES les cartes visibles, pas seulement les
    // places payées. emettreImpression() fait le tri : card_impression pour
    // toutes, vedette_impression pour les seules cartes qui portent la pill
    // ou appartiennent à un bloc entièrement payé.
    var vedettes = toutesLesCartes().filter(carteVisible);
    for (var i = 0; i < vedettes.length; i++) {
      var carte = vedettes[i];
      if (observees.has(carte)) continue;
      observees.add(carte);
      if (observateur) {
        observateur.observe(carte);
      } else {
        // Sans IntersectionObserver, on compte à l'affichage : moins juste,
        // mais mieux que de ne rien mesurer sur les vieux navigateurs.
        emettreImpression(carte);
      }
    }
  }

  // Le clic est capté au niveau du document : la grille est remplacée
  // plusieurs fois, un écouteur par carte serait perdu à chaque repaint.
  document.addEventListener(
    'click',
    function (evenement) {
      var lien = evenement.target && evenement.target.closest
        ? evenement.target.closest('a.grid-item__link')
        : null;
      if (!lien) return;
      var carte = lien.closest('.grid-product[data-product-id]');
      if (!carte || !estVedette(carte)) return;

      var bloc = blocDe(carte);
      var groupe = vedettesDuGroupe(bloc);
      var position = groupe.indexOf(carte);
      track(
        'vedette_clic',
        contexteVedette(carte, bloc, position >= 0 ? position + 1 : null, groupe.length),
        // La navigation part immédiatement : sendBeacon, sans attendre le lot.
        { immediat: true, beacon: true }
      );
    },
    true
  );

  // Les six événements sur lesquels le thème repeint la grille, plus le premier
  // rendu. `loven:sponsored-ready` est le seul qui garantisse que les pills
  // sont posées ; les autres couvrent les repeints ultérieurs. Aucun ne se
  // déclenche sur la page d'accueil : là, le premier rendu suffit, le bloc
  // étant servi en Liquid et jamais repeint.
  [
    'loven:sponsored-ready',
    'loven:dynamic-grid-rendered',
    'collection:availability',
    'collection:reloaded',
    'collection:destFiltered',
    'collection:mapViewportFiltered',
    'collection:mapSync'
  ].forEach(function (nom) {
    document.addEventListener(nom, function () {
      window.setTimeout(brancher, 0);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', brancher);
  } else {
    brancher();
  }
})();
